import type { ReactElement } from 'react'
import type { Route } from '@metro/routing'
import { DestinationButtons } from './DestinationButtons'
import type { RouteCardVariant, DestinationButtonsProps } from './DestinationButtons'
import { DestinationButton } from './DestinationButton'
import { SwapButton } from './SwapButton'
import { CloseButton } from './CloseButton'
import { MyLocationButton } from './MyLocationButton'
import { RouteResultsList } from './RouteResultsList'
import { SecondaryButton } from './SecondaryButton'
import type { LineDisplay } from './RouteVariantRow'
import { RouteLoading } from './RouteLoading'
import { SidePanelRouteLoading } from './SidePanelRouteLoading'
import { RouteEmpty } from './RouteEmpty'
import { RouteError } from './RouteError'
import { defaultRouteCardCopy, type RouteCardCopySet } from './copy'
import styles from './RouteCard.module.css'

/**
 * Discriminated union describing the lifecycle of the route-results sub-view
 * rendered below the always-visible From/To {@link DestinationButtons}.
 *
 * - `collapsed`: floating pills only (default — backwards-compatible with
 *   call sites that only pass From/To props).
 * - `loading`: skeleton placeholders ({@link RouteLoading} on phone,
 *   {@link SidePanelRouteLoading} on the tablet side panel).
 * - `results`: horizontal {@link RouteResultsList} + Route Details button.
 * - `empty`: {@link RouteEmpty} (zero variants returned).
 * - `error`: {@link RouteError} with an optional message.
 */
export type RouteStatusVM =
  | { kind: 'collapsed' }
  | { kind: 'loading' }
  | { kind: 'results'; routes: readonly Route[]; selectedIndex: number }
  | { kind: 'empty' }
  | { kind: 'error'; message?: string }

/**
 * Props for {@link RouteCard}.
 */
export interface RouteCardProps extends Omit<DestinationButtonsProps, 'variant'> {
  /** Layout variant — see {@link RouteCardVariant}. */
  readonly variant: RouteCardVariant
  /**
   * Override the localised header for the tablet variant. Ignored on phone.
   * @defaultValue `'Build route'`
   */
  readonly headerText?: string
  /**
   * Route lifecycle status. Drives the sub-view rendered below From/To.
   * @defaultValue `{ kind: 'collapsed' }`
   */
  readonly routeStatus?: RouteStatusVM
  /** Required when `routeStatus.kind === 'results'`. line_id → display info. */
  readonly lineLookup?: ReadonlyMap<string, LineDisplay>
  /** Required when `routeStatus.kind === 'results'`. Variant-tap handler. */
  readonly onSelectVariant?: (index: number) => void
  /**
   * Retry callback for empty / error states. Emits `retryTapped`
   * (spec §6 — emit-only this rebaseline).
   */
  readonly onRetry?: () => void
  /**
   * "Route Details" button callback (Results state). Emits
   * `openRouteDetails` (spec §6 — emit-only; the Route Details screen
   * routing is deferred). Optional — when omitted the button still
   * renders (no-op).
   */
  readonly onOpenRouteDetails?: () => void
  /**
   * My-location tap callback (spec-named output `myLocationTapped`).
   * When provided, the MyLocationButton is rendered on a top row ABOVE
   * the pills in the collapsed phone layout (Figma spec §2.1).
   */
  readonly onMyLocationTap?: () => void
  /**
   * Close handler for the route-built panel. When provided AND the
   * lifecycle is active, the panel's Destinations row renders a
   * {@link CloseButton}. Reuses the existing dismiss output (spec §6 —
   * no new case).
   */
  readonly onClose?: () => void
  /** Test seam clock for arrival HH:mm; passed through to {@link RouteResultsList}. */
  readonly clock?: () => Date
  /**
   * Localised copy set. Defaults to the English {@link defaultRouteCardCopy};
   * the metro-frontend host injects a catalog-backed set. en values are
   * byte-identical to the default, so the en render is unchanged.
   */
  readonly copy?: RouteCardCopySet
}

const DEFAULT_STATUS: RouteStatusVM = { kind: 'collapsed' }

/**
 * Top-level RouteCard container. Mirrors the iOS module shape.
 *
 * **Collapsed phone** (Figma spec §2.1): MyLocationButton on a top row,
 * trailing-aligned, ABOVE the `[From][To][Swap]` destinations row.
 *
 * **Route-built phone** (spec §2.5–2.9): an expanded panel (r24, double
 * shadow) with a Destinations row `[From][To][Swap][Close]` followed by
 * the state content (loading / results + Route Details / empty + Retry /
 * error + Retry). The map dim overlay + A/B badges are the host's
 * responsibility (map-level), not this panel.
 *
 * **Tablet**: unchanged from TASK-275 — "Build route" header + stacked
 * DestinationButtons; the route-built sub-views render below.
 *
 * From/To pills fire `onFromTap` / `onToTap` (TASK-318 — the host opens
 * Suggester for the slot); Swap / Clear / Close / Retry / Route
 * Details / MyLocation are wired via their own callbacks. When a tap
 * callback is omitted the pill body stays inert.
 */
export function RouteCard({
  variant,
  headerText,
  routeStatus = DEFAULT_STATUS,
  lineLookup,
  onSelectVariant,
  onRetry,
  onOpenRouteDetails,
  onMyLocationTap,
  onMyLocation,
  onClose,
  clock,
  copy = defaultRouteCardCopy,
  ...buttons
}: RouteCardProps): ReactElement {
  const header = headerText ?? copy.buildRoute
  const fromPlaceholder = buttons.fromPlaceholder ?? copy.from
  const toPlaceholder = buttons.toPlaceholder ?? copy.to
  const lifecycleActive = routeStatus.kind !== 'collapsed'
  // Spec output name is `myLocationTapped`; accept the new `onMyLocationTap`
  // and fall back to the legacy `onMyLocation` so existing call sites keep
  // working until they migrate.
  const myLocation = onMyLocationTap ?? onMyLocation

  // -- Phone, route-built: expanded panel ------------------------------
  if (variant === 'phone' && lifecycleActive) {
    return (
      <section
        className={`${styles.card} ${styles.phone} ${styles.phonePanel}`}
        data-testid="route-card-phone"
      >
        <div className={styles.destinationsRow}>
          <DestinationButton
            slot="from"
            station={buttons.from}
            placeholder={fromPlaceholder}
            variant="phone"
            onTap={buttons.onFromTap === undefined ? undefined : () => buttons.onFromTap?.()}
          />
          <DestinationButton
            slot="to"
            station={buttons.to}
            placeholder={toPlaceholder}
            variant="phone"
            onTap={buttons.onToTap === undefined ? undefined : () => buttons.onToTap?.()}
          />
          <SwapButton variant="phone" onClick={buttons.onSwap} ariaLabel={copy.swapAria} />
          {onClose !== undefined && (
            <CloseButton variant="phone" onClick={onClose} ariaLabel={copy.closeAria} />
          )}
        </div>
        {renderSubView({
          routeStatus,
          lineLookup,
          onSelectVariant,
          onRetry,
          onOpenRouteDetails,
          clock,
          variant,
          copy,
        })}
      </section>
    )
  }

  // -- Phone, collapsed: MyLocation above the pills --------------------
  if (variant === 'phone') {
    return (
      <section className={`${styles.card} ${styles.phone}`} data-testid="route-card-phone">
        {myLocation !== undefined && (
          <div className={styles.myLocationRow}>
            <MyLocationButton
              variant="phone"
              onClick={myLocation}
              isLoading={buttons.isResolvingMyLocation ?? false}
              ariaLabel={copy.myLocationAria}
            />
          </div>
        )}
        <div className={styles.destinationsRow}>
          <DestinationButton
            slot="from"
            station={buttons.from}
            placeholder={fromPlaceholder}
            variant="phone"
            onTap={buttons.onFromTap === undefined ? undefined : () => buttons.onFromTap?.()}
            onClear={buttons.onFromClear}
          />
          <DestinationButton
            slot="to"
            station={buttons.to}
            placeholder={toPlaceholder}
            variant="phone"
            onTap={buttons.onToTap === undefined ? undefined : () => buttons.onToTap?.()}
            onClear={buttons.onToClear}
          />
          <SwapButton variant="phone" onClick={buttons.onSwap} ariaLabel={copy.swapAria} />
        </div>
      </section>
    )
  }

  // -- Tablet: unchanged from TASK-275 ---------------------------------
  return (
    <section className={`${styles.card} ${styles.tablet}`} data-testid="route-card-tablet">
      <h2 className={styles.header}>{header}</h2>
      <DestinationButtons
        variant={variant}
        {...buttons}
        fromPlaceholder={fromPlaceholder}
        toPlaceholder={toPlaceholder}
        onMyLocation={myLocation}
      />
      {renderSubView({
        routeStatus,
        lineLookup,
        onSelectVariant,
        onRetry,
        onOpenRouteDetails,
        clock,
        variant,
        copy,
      })}
    </section>
  )
}

interface SubViewArgs {
  readonly routeStatus: RouteStatusVM
  readonly lineLookup: ReadonlyMap<string, LineDisplay> | undefined
  readonly onSelectVariant: ((index: number) => void) | undefined
  readonly onRetry: (() => void) | undefined
  readonly onOpenRouteDetails: (() => void) | undefined
  readonly clock: (() => Date) | undefined
  readonly variant: RouteCardVariant
  readonly copy: RouteCardCopySet
}

function renderSubView({
  routeStatus,
  lineLookup,
  onSelectVariant,
  onRetry,
  onOpenRouteDetails,
  clock,
  variant,
  copy,
}: SubViewArgs): ReactElement | null {
  switch (routeStatus.kind) {
    case 'collapsed':
      return null
    case 'loading':
      // The side panel loads in its results' shape (vertical variants +
      // details); the phone keeps the horizontal strip.
      return variant === 'tablet' ? <SidePanelRouteLoading /> : <RouteLoading />
    case 'results': {
      // TASK-412 (D mirror — user-confirmed iOS look @b23465455): the
      // TABLET/desktop side panel no longer renders the results variant
      // card here. The host renders RouteDetails inline below with a
      // LARGE variant selector (RouteResultsList variant="tablet") that
      // both lists every variant AND shows the fabric detail, so a second
      // selector in the RouteCard header would be a duplicate. The
      // tablet header keeps only "Build route" + From/To pills + swap.
      // Phone is unchanged (horizontal results carousel + Route Details
      // button below). Mirrors iOS hiding the RouteCard results list in
      // `.sidePanel`.
      if (variant === 'tablet') return null
      if (lineLookup === undefined || onSelectVariant === undefined) {
        if (process.env.NODE_ENV !== 'production') {
          console.warn(
            '[RouteCard] routeStatus.kind="results" requires both `lineLookup` and `onSelectVariant`; rendering nothing.',
          )
        }
        return null
      }
      return (
        <>
          <RouteResultsList
            routes={routeStatus.routes}
            selectedIndex={routeStatus.selectedIndex}
            lineLookup={lineLookup}
            onSelect={onSelectVariant}
            clock={clock}
            variant={variant}
            resultsListAria={copy.resultsListAria}
          />
          {/* "Route Details" button — PHONE ONLY. On phone it opens the
              RouteDetails modal (the button is the discoverable entry
              point). On the tablet/desktop side panel the host renders
              RouteDetails INLINE beneath this list and follows the
              selected variant automatically, so the button is redundant
              and hidden there (TASK-412 #4 — list-on-top / detail-below,
              no button). Mirrors iOS hiding the button in `.sidePanel`. */}
          {variant === 'phone' && (
            <div className={styles.detailsRow}>
              <SecondaryButton
                label={copy.routeDetails}
                onClick={onOpenRouteDetails}
                testId="route-details-button"
              />
            </div>
          )}
        </>
      )
    }
    case 'empty':
      return <RouteEmpty onRetry={onRetry} copy={copy} />
    case 'error':
      return <RouteError message={routeStatus.message} onRetry={onRetry} copy={copy} />
  }
}

export type { RouteCardVariant } from './DestinationButtons'
